/**
 * Frontend Logger
 * Replaces console.log to prevent sensitive data exposure in production
 */

const isDevelopment = process.env.NODE_ENV === 'development';

interface LogLevel {
  log: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
}

/**
 * Sanitizes sensitive data from log arguments
 */
function sanitizeLogData(args: unknown[]): unknown[] {
  return args.map(arg => {
    if (typeof arg === 'object' && arg !== null) {
      const sanitized: Record<string, unknown> = { ...(arg as Record<string, unknown>) };

      // Remove sensitive fields
      const sensitiveFields = ['password', 'token', 'secret', 'authorization', 'auth', 'apiKey', 'apikey'];

      for (const field of sensitiveFields) {
        if (sanitized[field]) {
          sanitized[field] = '[REDACTED]';
        }
        // Check nested objects
        for (const key in sanitized) {
          if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
            sanitized[key] = sanitizeLogData([sanitized[key]])[0];
          }
        }
      }

      return sanitized;
    }

    // Check if string contains sensitive data
    if (typeof arg === 'string') {
      const lowerArg = arg.toLowerCase();
      if (lowerArg.includes('password') || lowerArg.includes('token') || lowerArg.includes('secret')) {
        return '[REDACTED]';
      }
    }

    return arg;
  });
}

export const logger: LogLevel = {
  /**
   * Logs in development only
   */
  log: (...args: unknown[]) => {
    if (isDevelopment) {
      const sanitized = sanitizeLogData(args);
      console.log(...sanitized);
    }
  },

  /**
   * Errors always log (but sanitized)
   * Erros de rede são silenciados se não forem críticos
   */
  error: (...args: unknown[]) => {
    // Verificar se é um erro de rede que deve ser silenciado
    const firstArg = args[0];
    const isNetworkError = (typeof firstArg === 'string' && (
      firstArg.includes('ERR_NETWORK') ||
      firstArg.includes('ERR_SOCKET_NOT_CONNECTED') ||
      firstArg.includes('ERR_CONNECTION_RESET') ||
      firstArg.includes('Network Error') ||
      firstArg.includes('ECONNREFUSED')
    )) || (typeof firstArg === 'object' && firstArg !== null && (
      (firstArg as { code?: string }).code === 'ERR_NETWORK' ||
      (firstArg as { code?: string }).code === 'ERR_SOCKET_NOT_CONNECTED' ||
      (firstArg as { code?: string }).code === 'ERR_CONNECTION_RESET' ||
      (firstArg as { message?: string }).message?.includes('Network Error')
    ));
    
    // Não logar erros de rede esperados (backend não disponível)
    if (isNetworkError) {
      // Silenciar completamente erros de rede esperados
      return;
    }
    
    const sanitized = sanitizeLogData(args);
    console.error(...sanitized);
  },

  /**
   * Warnings in development only
   */
  warn: (...args: unknown[]) => {
    if (isDevelopment) {
      const sanitized = sanitizeLogData(args);
      console.warn(...sanitized);
    }
  },

  /**
   * Debug logs in development only
   */
  debug: (...args: unknown[]) => {
    if (isDevelopment) {
      const sanitized = sanitizeLogData(args);
      console.debug(...sanitized);
    }
  },

  /**
   * Info logs in development only
   */
  info: (...args: unknown[]) => {
    if (isDevelopment) {
      const sanitized = sanitizeLogData(args);
      console.info(...sanitized);
    }
  }
};

export default logger;

